import React from 'react';
import get from 'lodash/get';
import {
  Action,
  withInject,
} from '@piwikpro/platform';
import {
  Stack,
  Tooltip,
  Icon,
  withTranslation,
} from '@piwikpro/ui-components';
import { FormValidator } from '@piwikpro/form-crate';
import { TrackerSettings, TrackerSettingsResponse } from '../../../TrackerSettings';

export interface MaxSessionEventCountProps {
  appId: string
  trackerSettings: TrackerSettings
  appTrackerSettings: TrackerSettingsResponse
  globalTrackerSettings: TrackerSettingsResponse
  validation: FormValidator
  t: (key: string) => string
}

const defaultValue = 5000;

class MaxSessionEventCount extends React.Component<MaxSessionEventCountProps, {
  useGlobals: boolean
}> {
  static readonly fieldName = 'session_max_event_count';

  static readonly globalFieldName = `${this.fieldName}_global`;

  constructor(props: MaxSessionEventCountProps) {
    super(props);
    const { appTrackerSettings } = props;

    this.state = {
      useGlobals: appTrackerSettings.attributes?.[
        MaxSessionEventCount.fieldName
      ] === undefined,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSubmitCheckbox = this.handleSubmitCheckbox.bind(this);
  }

  async handleSubmit(values, {
    setErrors,
    setSubmitting,
    setFieldValue,
  }: {
    setErrors: Function
    setSubmitting: Function
    setFieldValue: Function
  }) {
    const {
      trackerSettings,
      appId,
    } = this.props;
    const field = Object.keys(values)[0];

    try {
      await trackerSettings.updateSettingsForApp(appId, {
        [MaxSessionEventCount.fieldName]: Number(values[MaxSessionEventCount.fieldName]),
      });
    } catch (err: any) {
      setErrors(err.errors);
      setFieldValue(
        field,
        this.getValue(),
        false,
      );
    }

    setSubmitting(false);
  }

  async handleSubmitCheckbox(
    values,
    { setFieldValue, setSubmitting }: {
      setSubmitting: Function
      setFieldValue: Function
    },
  ) {
    const {
      trackerSettings,
      appId,
    } = this.props;

    this.setState({
      useGlobals: values[MaxSessionEventCount.globalFieldName],
    });

    try {
      if (values[MaxSessionEventCount.globalFieldName]) {
        await trackerSettings.deleteSettingsForApp(
          appId,
          MaxSessionEventCount.fieldName,
        );
      } else {
        await trackerSettings.updateSettingsForApp(appId, {
          [MaxSessionEventCount.fieldName]: Number(this.getGlobalValue()),
        });
      }
    } catch (err) {
      setFieldValue(
        MaxSessionEventCount.globalFieldName,
        !values[MaxSessionEventCount.globalFieldName],
        false,
      );
      this.setState({
        useGlobals: !values[MaxSessionEventCount.globalFieldName],
      });
    }

    setSubmitting(false);
  }

  getGlobalValue() {
    const { globalTrackerSettings } = this.props;

    return get(globalTrackerSettings.attributes, MaxSessionEventCount.fieldName, defaultValue);
  }

  getValue() {
    const { appTrackerSettings } = this.props;
    const { useGlobals } = this.state;

    const globalValue = this.getGlobalValue();
    const applicationValue = get(
      appTrackerSettings.attributes,
      MaxSessionEventCount.fieldName,
      globalValue,
    );

    return useGlobals ? globalValue : applicationValue;
  }

  render() {
    const {
      validation,
      t,
    } = this.props;
    const { useGlobals } = this.state;

    const value = this.getValue();

    return (
      <Stack vertical>
        <Stack.Item fill>
          <Stack vertical spacing="narrow" horizontalAlignment="fill">
            <Stack.Item>
              <Action.AutoSavedNumberControl
                id="app-settings-max-session-event-count"
                label={t('apps.details.settings.session-limits.max-session-event-count.title')}
                info={t('apps.details.settings.session-limits.max-session-event-count.description')}
                name={MaxSessionEventCount.fieldName}
                initialValue={value}
                type="number"
                validationSchema={validation.createValidationSchema(validators => ({
                  [MaxSessionEventCount.fieldName]: validators.trackerSettings.sessionMaxEventCount,
                }))}
                disabled={useGlobals}
                onSubmit={this.handleSubmit}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack spacing="narrow">
            <Stack.Item>
              <Action.AutoSavedCheckboxControl
                id="app-settings-max-session-event-count-use-globals"
                onSubmit={this.handleSubmitCheckbox}
                initialValue={useGlobals}
                label={t('app.details.settings.general.use-global-settings.label')}
                name={MaxSessionEventCount.globalFieldName}
              />
            </Stack.Item>
            <Stack.Item>
              <Tooltip content={t('app.details.settings.general.use-global-settings.tooltip') as any}>
                <div>
                  <Icon name="info-circle" />
                </div>
              </Tooltip>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
  validation: 'FormCrate.validation',
})(
  withTranslation(['administration', 'platform'])(MaxSessionEventCount as any),
);
